import { Match, Option } from 'effect';
import { z } from 'zod';

import { ROLES } from '../AppConstants';

export type ROLE_NAME = (typeof ROLES)[keyof typeof ROLES];

export type User = {
    name: string;
    username: string;
    roles: Array<ROLE_NAME>;
    locationName: string;
    customerName: string;
    isAdmin?: boolean;
};

const UserSchema = z
    .object({
        // Allow name to be null due to failing check for scan-types page
        // ticket with the issue - SS-612
        name: z.string().nullable(),
        username: z.string(),
        roles: z.array(z.string()).transform((roles) => new Set(roles)),
        locationName: z.string(),
        customerName: z.string(),
        isAdmin: z.boolean().default(false)
    })
    .brand<'EnhancedUser'>();

export const AdminUserSchema = UserSchema.refine((user) => user.isAdmin)
    .transform((user) => ({ ...user, type: 'ADMIN' as const }))
    .brand<'Admin'>();

export const SiteAdminUserSchema = UserSchema.refine((user) => user.roles.has(ROLES.SITE_ADMIN))
    .transform((user) => ({ ...user, type: 'SITE_ADMIN' as const }))
    .brand<'SiteAdmin'>();

export type AdminUser = z.infer<typeof AdminUserSchema>;

export type SiteAdminUser = z.infer<typeof SiteAdminUserSchema>;

export const parseAdminOrSiteAdmin = (user: User): Option.Option<AdminUser | SiteAdminUser> => {
    const isAdminResult = AdminUserSchema.safeParse(user);
    const isSiteAdminResult = SiteAdminUserSchema.safeParse(user);

    return Match.value({ isAdminResult, isSiteAdminResult }).pipe(
        Match.when({ isAdminResult: { success: true } }, ({ isAdminResult: { data } }) => Option.some(data)),
        Match.when({ isSiteAdminResult: { success: true } }, ({ isSiteAdminResult: { data } }) => Option.some(data)),
        Match.orElse(() => Option.none())
    );
};
